import {
  Check as CheckIcon,
  Close as CloseIcon,
  GroupsOutlined as GroupsOutlinedIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import {
  CreateButton,
  Datagrid,
  DateField,
  EditButton,
  FunctionField,
  List,
  TextField,
  TopToolbar,
  useDataProvider,
  useGetIdentity,
  useNotify,
  usePermissions,
  useTranslate,
} from 'react-admin'
import EmptyState from '../components/EmptyState'
import PageHeader from '../components/PageHeader'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

const PendingInvitations = () => {
  const [invitations, setInvitations] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const translate = useTranslate()

  useEffect(() => {
    loadInvitations()
  }, [])

  const loadInvitations = async () => {
    try {
      const response = await deadlineFetch('/api/group-members/my-consent-requests', {
        headers: authHeaders(),
      })

      if (response.ok) {
        const result = await response.json()
        setInvitations(result.requests || [])
      }
    } catch (error) {
      console.error('Error loading invitations:', error)
    }
  }

  const handleConsent = async (
    membershipId: string,
    consent: 'granted' | 'denied',
    groupName: string,
  ) => {
    setLoading(true)
    try {
      const response = await deadlineFetch('/api/group-members/update-consent', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ membershipId, consent }),
      })

      const result = await response.json()

      if (response.ok) {
        notify(result.message, { type: 'success' })
        // Remove from invitations list
        setInvitations((prev) => prev.filter((inv) => inv.id !== membershipId))
        // Refresh the page to update the groups list
        window.location.reload()
      } else {
        notify(result.error || translate('resources.groups.notifications.consent_update_failed'), {
          type: 'error',
        })
      }
    } catch (error) {
      notify('resources.groups.notifications.consent_update_failed', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending_consent':
        return 'warning'
      case 'declined':
        return 'error'
      case 'invited':
        return 'info'
      default:
        return 'default'
    }
  }

  if (invitations.length === 0) return null

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {translate('resources.groups.ui.invitations')}
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('resources.groups.ui.col_group')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_invited_by')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_status')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_invited_date')}</TableCell>
              <TableCell>{translate('resources.groups.ui.col_actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invitations.map((invitation) => (
              <TableRow key={invitation.id}>
                <TableCell>
                  <Typography variant="subtitle2">
                    {typeof invitation.group === 'object'
                      ? invitation.group.name
                      : translate('pos.common.group_fallback', { id: invitation.group })}
                  </Typography>
                  {invitation.group?.description && (
                    <Typography variant="body2" color="text.secondary">
                      {invitation.group.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {invitation.invitedBy?.name ||
                    invitation.invitedBy?.email ||
                    translate('resources.groups.ui.unknown')}
                </TableCell>
                <TableCell>
                  <Chip
                    label={invitation.status.replace('_', ' ')}
                    color={getStatusColor(invitation.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{new Date(invitation.invitedAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  {invitation.status === 'pending_consent' && (
                    <Box>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckIcon />}
                        onClick={() =>
                          handleConsent(
                            invitation.id,
                            'granted',
                            typeof invitation.group === 'object'
                              ? invitation.group.name
                              : `Group ${invitation.group}`,
                          )
                        }
                        disabled={loading}
                        sx={{ mr: 1 }}
                      >
                        {translate('resources.groups.ui.accept')}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() =>
                          handleConsent(
                            invitation.id,
                            'denied',
                            typeof invitation.group === 'object'
                              ? invitation.group.name
                              : `Group ${invitation.group}`,
                          )
                        }
                        disabled={loading}
                      >
                        {translate('resources.groups.ui.decline')}
                      </Button>
                    </Box>
                  )}
                  {invitation.status === 'declined' && (
                    <Typography variant="body2" color="text.secondary">
                      {translate('resources.groups.ui.declined')}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}

const AdminsField = ({ record }: { record: any }) => {
  if (!record.admins || !Array.isArray(record.admins)) {
    return <span>-</span>
  }

  const adminNames = record.admins
    .map((admin: any) => admin.name || admin.email || `User ${admin.id || admin}`)
    .join(', ')

  return <span>{adminNames}</span>
}

const ListActions = () => {
  const { permissions } = usePermissions()

  // Only vetadmin can create groups, others are read-only
  if (permissions?.role !== 'vetadmin') {
    return <TopToolbar />
  }

  return (
    <TopToolbar>
      <CreateButton />
    </TopToolbar>
  )
}

/**
 * Empty state for someone who cannot create a Care Team.
 *
 * react-admin's default <Empty> reads `hasCreate` off the resource definition,
 * not the role, so it offered "Do you want to add one? CREATE" to every vet —
 * and GroupCreate then refused them. Say what is true instead, and point the
 * reader at Clinic Team, which is the page that actually gives colleagues
 * access to the clinic's patients (master accounts only, same gate the sidebar
 * uses — read off identity, never off `permissions`, which carries only role).
 */
const ReadOnlyEmpty = () => {
  const translate = useTranslate()
  const { identity } = useGetIdentity()
  const { permissions } = usePermissions()
  const isMaster =
    !!identity &&
    (permissions?.role === 'vet' || permissions?.role === 'vetadmin') &&
    !identity.masterAccount

  return (
    <Card sx={{ width: '100%' }}>
      <EmptyState
        icon={<GroupsOutlinedIcon />}
        title={translate('resources.groups.ui.empty_title')}
        description={
          <>
            {translate('resources.groups.ui.empty_readonly')}
            {isMaster && (
              <Box component="span" sx={{ display: 'block', mt: 1.5 }}>
                {translate('resources.groups.ui.empty_use_clinic_team')}{' '}
                <Link href="#/clinic-team">{translate('pos.menu.clinic_team')}</Link>
              </Box>
            )}
          </>
        }
      />
    </Card>
  )
}

const GroupList = () => {
  const { permissions } = usePermissions()
  const translate = useTranslate()

  // Same gate as ListActions. `empty={undefined}` keeps react-admin's default
  // empty state (with its Create button) for the one role that can use it.
  const canCreate = permissions?.role === 'vetadmin'

  return (
    <div>
      <PageHeader
        title={
          permissions?.role === 'siteadmin'
            ? translate('pos.menu.all_teams_readonly')
            : translate('pos.menu.care_teams')
        }
        subtitle={translate('pos.menu.care_teams_hint')}
      />
      <List actions={<ListActions />} empty={canCreate ? undefined : <ReadOnlyEmpty />}>
        <Datagrid
          rowClick={permissions?.role === 'vet' ? false : 'show'}
          bulkActionButtons={permissions?.role === 'vet' ? false : undefined}
        >
          <TextField source="name" />
          <TextField source="description" />
          <FunctionField
            label={translate('resources.groups.ui.administrators')}
            render={(record: any) => <AdminsField record={record} />}
          />
          <DateField source="createdAt" showTime />
          {/* Only vetadmin can edit groups */}
          {permissions?.role === 'vetadmin' && <EditButton />}
        </Datagrid>
      </List>

      {/* Show pending invitations for regular vets */}
      {permissions?.role === 'vet' && <PendingInvitations />}
    </div>
  )
}

export default GroupList
